import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { createBanner, deleteBanner, fetchBanners } from './hooks/hooks';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// Icons (you can replace these with your preferred icon library)
const AddIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const ArrowBackIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

const CloudUploadIcon = () => (
  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
  </svg>
);

const DeleteIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-64">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

const validationSchema = Yup.object({
  image: Yup.mixed()
    .required('Image is required')
    .test('fileType', 'Only image files are allowed', (value) => {
      if (value) {
        return ['image/jpeg', 'image/png', 'image/gif'].includes(value.type);
      }
      return true;
    }),
});

function Banner() {
  const queryClient = useQueryClient();
  const { data: banners, error, isLoading, refetch } = useQuery({
    queryKey: ['banners'],
    queryFn: fetchBanners,
  });

  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "" });

  const deleteMutation = useMutation({
    mutationFn: deleteBanner,
    onSuccess: () => {
      queryClient.invalidateQueries(["deletBanners"]);
      setToast({ open: true, message: "Banner deleted successfully!", severity: "success" });
    },
    onError: (error) => {
      setToast({ open: true, message: "Failed to delete banner. Try again.", severity: "error" });
    }
  });

  const mutation = useMutation({
    mutationFn: createBanner,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["newBanner"] });
      setOpen(false);
      setToast({ open: true, message: "Banner added successfully!", severity: "success" });
      refetch();
    },
    onError: (error) => {
      setToast({ open: true, message: "Failed to add Banner. Try again.", severity: "error" });
    }
  });

  const handleDelete = (id) => {
    deleteMutation.mutate(id);
  };

  const formik = useFormik({
    initialValues: {
      image: null,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      mutation.mutate(values);
    },
  });

  const toggleDrawer = (state) => () => {
    setOpen(state);
  };

  // Toast Component
  const Toast = ({ toast, onClose }) => {
    if (!toast.open) return null;
    
    const bgColor = toast.severity === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';
    const textColor = toast.severity === 'success' ? 'text-green-800' : 'text-red-800';
    
    return (
      <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top duration-300">
        <div className={`${bgColor} ${textColor} border rounded-lg px-4 py-3 shadow-lg flex items-center space-x-2 max-w-sm`}>
          <span className="text-sm font-medium">{toast.message}</span>
          <button 
            onClick={onClose}
            className="ml-auto text-gray-400 hover:text-gray-600"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  // Upload Form Component
  const UploadForm = ({ showBackButton = false }) => (
    <div className="max-w-2xl mx-auto">
      {showBackButton && (
        <button
          onClick={toggleDrawer(false)}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 mb-6 transition-colors"
        >
          <ArrowBackIcon />
          <span className="text-sm font-medium">Back to Banner List</span>
        </button>
      )}
      
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-8 py-6 border-b border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Add New Banner</h2>
          <p className="text-gray-600">Make your app more engaging with a stunning banner. Add one now!</p>
        </div>
        
        <div className="p-8">
          <div className="space-y-6">
            {/* Image Upload Field */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Banner Image
              </label>
              <div className="relative">
                <input
                  ref={(ref) => { window.fileInputRef = ref; }}
                  type="file"
                  name="image"
                  onChange={(event) => {
                    const file = event.target.files[0];
                    if (file) {
                      formik.setFieldValue('image', file);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  accept="image/*"
                  className="hidden"
                />
                <div 
                  onClick={() => window.fileInputRef?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 hover:bg-blue-50 transition-all duration-200 cursor-pointer"
                >
                  <CloudUploadIcon />
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700">Click to upload</p>
                    <p className="text-xs text-gray-500 mt-1">JPEG, PNG or GIF (Max 10MB)</p>
                  </div>
                </div>
              </div>
              
              {formik.touched.image && formik.errors.image && (
                <p className="text-red-500 text-xs mt-2 font-medium">Image is required</p>
              )}
              
              {formik.values.image && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                    Selected File: {formik.values.image.name}
                  </p>
                  <div className="relative">
                    <img
                      src={URL.createObjectURL(formik.values.image)}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-lg shadow-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              onClick={(e) => {
                e.preventDefault();
                formik.handleSubmit(e);
              }}
              disabled={mutation.isLoading}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {mutation.isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Adding Banner...</span>
                </div>
              ) : (
                'Add New Banner'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
            <h3 className="text-lg font-semibold text-red-800 mb-2">Error Loading Banners</h3>
            <p className="text-red-600">Please try again later.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {banners?.length === 0 ? (
          <UploadForm />
        ) : open ? (
          <UploadForm showBackButton={true} />
        ) : (
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Banner Management</h1>
              <p className="text-gray-600 mb-6">Upload and organize banners to enhance your app's visual appeal.</p>
              
              <button
                onClick={toggleDrawer(true)}
                className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-800 font-semibold underline underline-offset-2 transition-colors"
              >
                <AddIcon />
                <span>Add New Banner</span>
              </button>
            </div>

            {/* Banners List */}
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 uppercase tracking-wide">Banners List</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {banners?.map((banner) => (
                  <div key={banner.id} className="group relative bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-200">
                    <div className="relative">
                      <img
                        src={`https://api.jamboricg.com${banner.image}`}
                        alt="Banner"
                        className="w-full h-40 object-cover"
                      />
                      
                      {/* Delete Button */}
                      <button
                        onClick={() => handleDelete(banner.id)}
                        disabled={deleteMutation.isLoading}
                        className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition-all duration-200 disabled:opacity-50"
                      >
                        <DeleteIcon />
                      </button>
                    </div>
                    
                    <div className="p-4">
                      <p className="text-xs text-gray-500 font-medium">
                        Created: {new Date(banner?.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <Toast 
          toast={toast} 
          onClose={() => setToast({ open: false, message: "", severity: "" })} 
        />
      </div>
    </div>
  );
}

export default Banner;